import { createElement, useEffect, type ReactNode } from "react"
import { usePage } from "@inertiajs/react"
import { Toaster, toast } from "sonner"
import { Header } from "./Header"
import { CurrentUserProvider, useCurrentUser } from "../../hooks/useCurrentUser"

const URL_REGEX = /(https?:\/\/[^\s]+)/g

function linkifyMessage(message: string): string | ReactNode {
  if (!URL_REGEX.test(message)) return message

  const parts = message.split(URL_REGEX)
  return createElement(
    "span",
    null,
    ...parts.map((part, i) =>
      URL_REGEX.test(part)
        ? createElement(
            "a",
            {
              key: i,
              href: part,
              target: "_blank",
              rel: "noopener noreferrer",
              className: "underline font-medium",
            },
            part
          )
        : part
    )
  )
}

interface PublicLayoutProps {
  children: ReactNode
}

interface PageProps {
  flash?: Record<string, string> | null
  [key: string]: unknown
}

// Pages that hide the header
const HIDE_HEADER_PAGES = [
  "CoffeeShop/Show",
  "User/Collection",
  "LoyaltyCards/Claim",
  "CoffeePasses/Claim",
]

// Survives PublicLayout remounts so /me and the next Inertia visit
// cannot replay the same flash. Per-component refs reset on remount
// and the two sources do not share a ref.
const shownFlashKeys = new Set<string>()

function showSharedFlash(flash: Record<string, string> | null | undefined) {
  if (!flash || Object.keys(flash).length === 0) return

  const key = JSON.stringify(flash)
  if (shownFlashKeys.has(key)) return
  shownFlashKeys.add(key)

  if (flash.notice) {
    toast.success(linkifyMessage(flash.notice), {
      id: `notice:${flash.notice}`,
    })
  }
  if (flash.alert) {
    toast.error(linkifyMessage(flash.alert), { id: `alert:${flash.alert}` })
  }
}

function PublicLayoutInner({ children }: PublicLayoutProps) {
  const page = usePage<PageProps>()
  const component = page.component
  const hideHeader = HIDE_HEADER_PAGES.includes(component)

  const { data: meData } = useCurrentUser()

  // Page props, or /me when the HTML was served from cache without flash.
  const flash = page.props.flash
  const meFlash = meData?.flash

  useEffect(() => {
    showSharedFlash(flash)
  }, [flash])

  useEffect(() => {
    showSharedFlash(meFlash)
  }, [meFlash])

  return (
    <>
      <Header hidden={hideHeader} />
      {children}
      <Toaster position="bottom-right" richColors />
    </>
  )
}

export default function PublicLayout({ children }: PublicLayoutProps) {
  return (
    <CurrentUserProvider>
      <PublicLayoutInner>{children}</PublicLayoutInner>
    </CurrentUserProvider>
  )
}
